/**
 * API Service Layer for Staff Portal
 * Connects to poker-crm-backend NestJS API
 * 
 * SECURITY: All data flows through backend API only.
 * No direct Supabase access from frontend.
 */

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  'https://poker-crm-backend-gihm.onrender.com/api';


// Storage keys
const STORAGE_KEYS = {
  USER_ID: 'userId',
  USER_EMAIL: 'userEmail',
  USER_ROLE: 'userRole',
  CLUB_ID: 'clubId',
  TENANT_ID: 'tenantId',
  AUTH_TOKEN: 'authToken',
};

/**
 * Get authentication headers
 * @param {string|null|undefined} clubIdOverride - When calling /clubs/:id/..., pass that id so x-club-id matches the URL (Super Admin has no club in localStorage until a club is selected).
 */
export const getAuthHeaders = (clubIdOverride) => {
  const headers = {
    'Content-Type': 'application/json',
  };

  const userId = localStorage.getItem(STORAGE_KEYS.USER_ID);
  const storedClubId = localStorage.getItem(STORAGE_KEYS.CLUB_ID);
  const clubId =
    clubIdOverride !== undefined &&
    clubIdOverride !== null &&
    String(clubIdOverride).trim() !== ''
      ? String(clubIdOverride).trim()
      : storedClubId;
  const tenantId = localStorage.getItem(STORAGE_KEYS.TENANT_ID);
  const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);

  if (userId) headers['x-user-id'] = userId;
  if (clubId) headers['x-club-id'] = clubId;
  if (tenantId) headers['x-tenant-id'] = tenantId;
  if (token) headers['Authorization'] = `Bearer ${token}`;

  return headers;
};

/**
 * Make API request
 */
export const apiRequest = async (endpoint, options = {}) => {
  const { clubIdOverride, ...fetchOptions } = options;
  const url = `${API_BASE_URL}${endpoint}`;
  const config = {
    ...fetchOptions,
    headers: {
      ...getAuthHeaders(clubIdOverride),
      ...fetchOptions.headers,
    },
  };

  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      let error;
      try {
        error = await response.json();
      } catch (e) {
        error = { message: response.statusText || `API Error: ${response.status}` };
      }
      if (response.status === 401) {
        const isLoginEndpoint = endpoint.includes('/auth/login');
        const isPasswordResetRequired = (error.message || '').toLowerCase().includes('password reset');
        // Only fire session:expired for genuine auth expiry — not for password-reset-required
        if (!isLoginEndpoint && !isPasswordResetRequired) {
          window.dispatchEvent(new CustomEvent('session:expired'));
        }
        throw new Error(error.message || 'Invalid email or password');
      }
      throw new Error(error.message || `API Error: ${response.status}`);
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('API Request Error:', error);
    throw error;
  }
};

/**
 * Many club list endpoints return either a JSON array or { data | requests | items | results }.
 */
export function normalizeApiListResponse(data) {
  if (Array.isArray(data)) return data;
  if (data == null) return [];
  if (Array.isArray(data.requests)) return data.requests;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.results)) return data.results;
  if (Array.isArray(data.entries)) return data.entries;
  return [];
}

// =============================================================================
// AUTHENTICATION API
// =============================================================================

export const authAPI = {
  /**
   * Staff login
   */
  login: async (email, password) => {
    const data = await apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    // Wipe any previous user's tokens before storing new ones
    // (prevents stale super_adminuser / master_adminuser etc. from lingering)
    localStorage.clear();
    sessionStorage.clear();

    // Store auth data
    if (data.user) {
      localStorage.setItem(STORAGE_KEYS.USER_ID, data.user.id);
      localStorage.setItem(STORAGE_KEYS.USER_EMAIL, data.user.email);
      if (data.token) {
        localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, data.token);
        // Backward compatibility for legacy fetch helpers/components.
        localStorage.setItem('token', data.token);
      }
      
      // Store full user object for profile display
      localStorage.setItem('user', JSON.stringify({
        id: data.user.id,
        email: data.user.email,
        name: data.user.displayName || data.user.email,
        displayName: data.user.displayName,
        isMasterAdmin: data.user.isMasterAdmin || false,
        mustResetPassword: data.user.mustResetPassword || false,
        staffId: data.user.staffId // Store staff ID for notifications
      }));
      
      // Store club role if exists
      if (data.clubRoles && data.clubRoles.length > 0) {
        const primaryRole = data.clubRoles[0];
        localStorage.setItem(STORAGE_KEYS.USER_ROLE, primaryRole.role);
        localStorage.setItem(STORAGE_KEYS.CLUB_ID, primaryRole.club.id);
        localStorage.setItem(STORAGE_KEYS.TENANT_ID, primaryRole.club.tenantId);
      }
      // Store tenant role if exists
      else if (data.tenantRoles && data.tenantRoles.length > 0) {
        const primaryRole = data.tenantRoles[0];
        localStorage.setItem(STORAGE_KEYS.USER_ROLE, primaryRole.role);
        localStorage.setItem(STORAGE_KEYS.TENANT_ID, primaryRole.tenant.id);
      }
      // Master admin
      else if (data.user.isMasterAdmin) {
        localStorage.setItem(STORAGE_KEYS.USER_ROLE, 'MASTER_ADMIN');
      }
    }

    return data;
  },

  /**
   * Logout
   */
  logout: () => {
    localStorage.clear();
    sessionStorage.clear();
  },

  /**
   * Get current user data
   */
  getCurrentUser: () => {
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : {};
    
    return {
      userId: localStorage.getItem(STORAGE_KEYS.USER_ID),
      email: localStorage.getItem(STORAGE_KEYS.USER_EMAIL),
      role: localStorage.getItem(STORAGE_KEYS.USER_ROLE),
      clubId: localStorage.getItem(STORAGE_KEYS.CLUB_ID),
      tenantId: localStorage.getItem(STORAGE_KEYS.TENANT_ID),
      isMasterAdmin: user.isMasterAdmin || false,
      displayName: user.displayName || null,
    };
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated: () => {
    return !!localStorage.getItem(STORAGE_KEYS.USER_ID);
  },
};

// =============================================================================
// CLUBS API
// =============================================================================

export const clubsAPI = {
  /**
   * Get all clubs
   */
  getClubs: async () => {
    return await apiRequest('/clubs');
  },

  /**
   * Get club by ID
   */
  getClub: async (clubId) => {
    return await apiRequest(`/clubs/${clubId}`);
  },

  /**
   * Get player by ID
   */
  getPlayer: async (clubId, playerId) => {
    return await apiRequest(`/clubs/${clubId}/players/${playerId}`);
  },

  updatePlayer: async (clubId, playerId, playerData) => {
    return await apiRequest(`/clubs/${clubId}/players/${playerId}`, {
      method: 'PUT',
      body: JSON.stringify(playerData),
    });
  },

  /**
   * Get club revenue data (revenue, rake, tips)
   */
  getClubRevenue: async (clubId) => {
    return await apiRequest(`/clubs/${clubId}/revenue`);
  },

  /**
   * Create club
   */
  createClub: async (clubData) => {
    return await apiRequest('/clubs', {
      method: 'POST',
      body: JSON.stringify(clubData),
    });
  },

  /**
   * Update club
   */
  updateClub: async (clubId, clubData) => {
    return await apiRequest(`/clubs/${clubId}`, {
      method: 'PUT',
      body: JSON.stringify(clubData),
    });
  },

  /**
   * Get active tables for rake collection
   */
  getActiveTablesForRakeCollection: async (clubId) => {
    return await apiRequest(`/clubs/${clubId}/rake-collections/active-tables`);
  },

  /**
   * Create rake collection
   */
  createRakeCollection: async (clubId, collectionData) => {
    return await apiRequest(`/clubs/${clubId}/rake-collections`, {
      method: 'POST',
      body: JSON.stringify(collectionData),
    });
  },

  /**
   * Get rake collections with filters
   */
  getRakeCollections: async (clubId, query = {}) => {
    // Remove empty string values from query
    const cleanQuery = Object.fromEntries(
      Object.entries(query).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
    );
    const queryString = new URLSearchParams(cleanQuery).toString();
    return await apiRequest(`/clubs/${clubId}/rake-collections${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get rake collection stats
   */
  getRakeCollectionStats: async (clubId, startDate, endDate) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return await apiRequest(`/clubs/${clubId}/rake-collections/stats${params.toString() ? `?${params.toString()}` : ''}`);
  },

  /**
   * Get buy-out requests
   */
  getBuyOutRequests: async (clubId, queryParams = {}) => {
    const params = new URLSearchParams();
    Object.entries(queryParams).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.append(k, String(v));
    });
    const qs = params.toString();
    const raw = await apiRequest(`/clubs/${clubId}/buyout-requests${qs ? `?${qs}` : ''}`, {
      clubIdOverride: clubId,
    });
    return normalizeApiListResponse(raw);
  },

  /**
   * Approve buy-out request
   */
  approveBuyOutRequest: async (clubId, requestId, data) => {
    return await apiRequest(`/clubs/${clubId}/buyout-requests/${requestId}/approve`, {
      method: 'POST',
      body: JSON.stringify(data),
      clubIdOverride: clubId,
    });
  },

  /**
   * Reject buy-out request
   */
  rejectBuyOutRequest: async (clubId, requestId, data) => {
    return await apiRequest(`/clubs/${clubId}/buyout-requests/${requestId}/reject`, {
      method: 'POST',
      body: JSON.stringify(data),
      clubIdOverride: clubId,
    });
  },

  /**
   * Get buy-in requests.
   * @param {string} clubId
   * @param {Record<string, string|number|boolean>} [queryParams] Optional query string, e.g. `{ tableChipQueue: 'true' }` if the backend filters table vs club wallet rows.
   */
  getBuyInRequests: async (clubId, queryParams = {}) => {
    const params = new URLSearchParams();
    Object.entries(queryParams).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.append(k, String(v));
    });
    const qs = params.toString();
    const raw = await apiRequest(`/clubs/${clubId}/buyin-requests${qs ? `?${qs}` : ''}`, {
      clubIdOverride: clubId,
    });
    return normalizeApiListResponse(raw);
  },

  /**
   * Approve buy-in request
   */
  approveBuyInRequest: async (clubId, requestId, data) => {
    return await apiRequest(`/clubs/${clubId}/buyin-requests/${requestId}/approve`, {
      method: 'POST',
      body: JSON.stringify(data),
      clubIdOverride: clubId,
    });
  },

  /**
   * Reject buy-in request
   */
  rejectBuyInRequest: async (clubId, requestId, data) => {
    return await apiRequest(`/clubs/${clubId}/buyin-requests/${requestId}/reject`, {
      method: 'POST',
      body: JSON.stringify(data),
      clubIdOverride: clubId,
    });
  },

  /**
   * Get attendance records
   */
  getAttendanceRecords: async (clubId, startDate, endDate, staffId) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (staffId) params.append('staffId', staffId);
    return await apiRequest(`/clubs/${clubId}/attendance${params.toString() ? `?${params.toString()}` : ''}`);
  },

  /**
   * Get attendance stats
   */
  getAttendanceStats: async (clubId, startDate, endDate) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return await apiRequest(`/clubs/${clubId}/attendance/stats${params.toString() ? `?${params.toString()}` : ''}`);
  },

  /**
   * Get salary payments (for HR - read-only)
   */
  getSalaryPayments: async (clubId, query = {}) => {
    const cleanQuery = Object.fromEntries(
      Object.entries(query).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
    );
    const queryString = new URLSearchParams(cleanQuery).toString();
    return await apiRequest(`/clubs/${clubId}/payroll/salary${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Create attendance record
   */
  createAttendanceRecord: async (clubId, data) => {
    return await apiRequest(`/clubs/${clubId}/attendance`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Get daily roster for attendance
   */
  getDailyRoster: async (clubId, date) => {
    return await apiRequest(`/clubs/${clubId}/attendance/daily-roster?date=${date}`);
  },

  /**
   * Bulk create attendance records
   */
  bulkCreateAttendance: async (clubId, entries, allowOffDayExtraAttendance = false) => {
    return await apiRequest(`/clubs/${clubId}/attendance/bulk`, {
      method: 'POST',
      body: JSON.stringify({ entries, allowOffDayExtraAttendance }),
    });
  },

  bulkDeleteAttendanceRecords: async (clubId, recordIds) => {
    return await apiRequest(`/clubs/${clubId}/attendance/bulk-delete`, {
      method: 'POST',
      body: JSON.stringify({ recordIds }),
    });
  },

  deleteAttendanceRecord: async (clubId, recordId) => {
    return await apiRequest(`/clubs/${clubId}/attendance/${recordId}`, {
      method: 'DELETE',
    });
  },

  updateAttendanceRecord: async (clubId, recordId, data) => {
    return await apiRequest(`/clubs/${clubId}/attendance/${recordId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
};

// =============================================================================
// PLAYERS API
// =============================================================================

export const playersAPI = {
  /**
   * Get all players in a club
   */
  getPlayers: async (clubId) => {
    return await apiRequest(`/clubs/${clubId}/players`);
  },

  /**
   * Get player by ID
   */
  getPlayer: async (clubId, playerId) => {
    return await apiRequest(`/clubs/${clubId}/players/${playerId}`);
  },

  /**
   * Create player
   */
  createPlayer: async (clubId, playerData) => {
    return await apiRequest(`/clubs/${clubId}/players`, {
      method: 'POST',
      body: JSON.stringify(playerData),
    });
  },

  /**
   * Update player
   */
  updatePlayer: async (clubId, playerId, playerData) => {
    return await apiRequest(`/clubs/${clubId}/players/${playerId}`, {
      method: 'PUT',
      body: JSON.stringify(playerData),
    });
  },

  /**
   * Update player KYC status
   */
  updateKycStatus: async (clubId, playerId, status, approvedBy) => {
    return await apiRequest(`/clubs/${clubId}/players/${playerId}/kyc-status`, {
      method: 'PUT',
      body: JSON.stringify({ kycStatus: status, approvedBy }),
    });
  },

  /**
   * Get KYC pending players
   */
  getKycPending: async (clubId) => {
    const players = await apiRequest(`/clubs/${clubId}/players`);
    return players.filter(p => p.kycStatus === 'pending');
  },

  /**
   * Get pending approval players
   */
  getPendingApprovalPlayers: async (clubId) => {
    return await apiRequest(`/clubs/${clubId}/players-pending-approval`);
  },

  /**
   * Get suspended players
   */
  getSuspendedPlayers: async (clubId) => {
    return await apiRequest(`/clubs/${clubId}/players-suspended`);
  },
};

// =============================================================================
// TABLES API
// =============================================================================

export const tablesAPI = {
  /**
   * Get all tables in a club
   */
  getTables: async (clubId) => {
    return await apiRequest(`/clubs/${clubId}/tables`);
  },

  /**
   * Get table by ID
   */
  getTable: async (clubId, tableId) => {
    return await apiRequest(`/clubs/${clubId}/tables/${tableId}`);
  },

  /**
   * Create table
   */
  createTable: async (clubId, tableData) => {
    return await apiRequest(`/clubs/${clubId}/tables`, {
      method: 'POST',
      body: JSON.stringify(tableData),
    });
  },

  /**
   * Update table
   */
  updateTable: async (clubId, tableId, tableData) => {
    return await apiRequest(`/clubs/${clubId}/tables/${tableId}`, {
      method: 'PUT',
      body: JSON.stringify(tableData),
    });
  },

  /**
   * Delete table
   */
  deleteTable: async (clubId, tableId) => {
    return await apiRequest(`/clubs/${clubId}/tables/${tableId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Assign seat at table
   */
  assignSeat: async (clubId, tableId, assignmentData) => {
    return await apiRequest(`/clubs/${clubId}/tables/${tableId}/assign-seat`, {
      method: 'POST',
      body: JSON.stringify(assignmentData),
    });
  },

  /**
   * Pause table session
   */
  pauseSession: async (clubId, tableId) => {
    return await apiRequest(`/clubs/${clubId}/tables/${tableId}/pause-session`, {
      method: 'POST',
    });
  },

  /**
   * Resume table session
   */
  resumeSession: async (clubId, tableId) => {
    return await apiRequest(`/clubs/${clubId}/tables/${tableId}/resume-session`, {
      method: 'POST',
    });
  },

  /**
   * End table session
   */
  endSession: async (clubId, tableId) => {
    return await apiRequest(`/clubs/${clubId}/tables/${tableId}/end-session`, {
      method: 'POST',
    });
  },

  /**
   * Get seated players for a table
   */
  getSeatedPlayersForTable: async (clubId, tableId) => {
    return await apiRequest(`/clubs/${clubId}/tables/${tableId}/seated-players`);
  },

  /**
   * Settle all players and end session
   */
  settleAndEndSession: async (clubId, tableId, data) => {
    // Support both array (legacy) and object format
    const body = Array.isArray(data)
      ? { settlements: data }
      : { settlements: data.settlements, rakeAmount: data.rakeAmount };
    return await apiRequest(`/clubs/${clubId}/tables/${tableId}/settle-and-end`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /**
   * Update session parameters
   */
  updateSessionParams: async (clubId, tableId, params) => {
    return await apiRequest(`/clubs/${clubId}/tables/${tableId}/session-params`, {
      method: 'PATCH',
      body: JSON.stringify(params),
    });
  },
};

// =============================================================================
// WAITLIST API
// =============================================================================

export const waitlistAPI = {
  /**
   * Get all waitlist entries
   */
  getWaitlist: async (clubId, status = null) => {
    const query = status ? `?status=${status}` : '';
    const raw = await apiRequest(`/clubs/${clubId}/waitlist${query}`, {
      clubIdOverride: clubId,
    });
    return normalizeApiListResponse(raw);
  },

  /**
   * Add to waitlist
   */
  addToWaitlist: async (clubId, waitlistData) => {
    return await apiRequest(`/clubs/${clubId}/waitlist`, {
      method: 'POST',
      body: JSON.stringify(waitlistData),
    });
  },

  /**
   * Update waitlist entry
   */
  updateWaitlist: async (clubId, entryId, updateData) => {
    return await apiRequest(`/clubs/${clubId}/waitlist/${entryId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData),
    });
  },

  /**
   * Update waitlist entry (alias)
   */
  updateWaitlistEntry: async (clubId, entryId, updateData) => {
    return await apiRequest(`/clubs/${clubId}/waitlist/${entryId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData),
    });
  },

  /**
   * Unseat player from table
   */
  unseatPlayerFromTable: async (clubId, entryId, opts = {}) => {
    const requeue = opts.requeue !== false;
    return await apiRequest(`/clubs/${clubId}/waitlist/${entryId}/unseat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ requeue }),
    });
  },

  /**
   * Seat player from waitlist
   */
  seatPlayer: async (clubId, entryId, tableNumber, seatedBy) => {
    return await apiRequest(`/clubs/${clubId}/waitlist/${entryId}`, {
      method: 'PUT',
      body: JSON.stringify({
        status: 'SEATED',
        tableNumber,
        seatedBy,
        seatedAt: new Date().toISOString(),
      }),
    });
  },

  /**
   * Assign seat to waitlist entry
   */
  assignSeat: async (clubId, entryId, assignmentData) => {
    return await apiRequest(`/clubs/${clubId}/waitlist/${entryId}/assign-seat`, {
      method: 'POST',
      body: JSON.stringify(assignmentData),
    });
  },

  /**
   * Cancel waitlist entry
   */
  cancelWaitlist: async (clubId, entryId) => {
    return await apiRequest(`/clubs/${clubId}/waitlist/${entryId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Delete waitlist entry (alias)
   */
  deleteWaitlistEntry: async (clubId, entryId) => {
    return await apiRequest(`/clubs/${clubId}/waitlist/${entryId}`, {
      method: 'DELETE',
    });
  },
};

// =============================================================================
// CREDIT API
// =============================================================================

export const creditAPI = {
  /**
   * Get all credit requests
   */
  getCreditRequests: async (clubId, status = null) => {
    const query = status ? `?status=${status}` : '';
    return await apiRequest(`/clubs/${clubId}/credit-requests${query}`);
  },

  /**
   * Create credit request
   */
  createCreditRequest: async (clubId, requestData) => {
    return await apiRequest(`/clubs/${clubId}/credit-requests`, {
      method: 'POST',
      body: JSON.stringify(requestData),
    });
  },

  /**
   * Approve credit request
   */
  approveCreditRequest: async (clubId, requestId, approvedBy) => {
    return await apiRequest(`/clubs/${clubId}/credit-requests/${requestId}/approve`, {
      method: 'PUT',
      body: JSON.stringify({ approvedBy }),
    });
  },

  /**
   * Reject credit request
   */
  rejectCreditRequest: async (clubId, requestId, reason) => {
    return await apiRequest(`/clubs/${clubId}/credit-requests/${requestId}/reject`, {
      method: 'PUT',
      body: JSON.stringify({ reason }),
    });
  },

  /**
   * Update credit visibility
   */
  updateCreditVisibility: async (clubId, requestId, visibleToPlayer) => {
    return await apiRequest(`/clubs/${clubId}/credit-requests/${requestId}/visibility`, {
      method: 'PUT',
      body: JSON.stringify({ visibleToPlayer }),
    });
  },
};

// =============================================================================
// FNB API
// =============================================================================

export const fnbAPI = {
  /**
   * Get menu items
   */
  getMenu: async (clubId) => {
    return await apiRequest(`/clubs/${clubId}/fnb/menu`);
  },

  /**
   * Create menu item
   */
  createMenuItem: async (clubId, itemData) => {
    return await apiRequest(`/clubs/${clubId}/fnb/menu`, {
      method: 'POST',
      body: JSON.stringify(itemData),
    });
  },

  /**
   * Update menu item
   */
  updateMenuItem: async (clubId, itemId, itemData) => {
    return await apiRequest(`/clubs/${clubId}/fnb/menu/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(itemData),
    });
  },

  /**
   * Get all orders
   */
  getOrders: async (clubId, status = null, page = 1, limit = 10) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    return await apiRequest(`/clubs/${clubId}/fnb/orders?${params.toString()}`);
  },

  /**
   * Create order
   */
  createOrder: async (clubId, orderData) => {
    return await apiRequest(`/clubs/${clubId}/fnb/orders`, {
      method: 'POST',
      body: JSON.stringify(orderData),
    });
  },

  /**
   * Update order status
   */
  updateOrderStatus: async (clubId, orderId, status, processedBy) => {
    return await apiRequest(`/clubs/${clubId}/fnb/orders/${orderId}`, {
      method: 'PUT',
      body: JSON.stringify({ status, processedBy }),
    });
  },

  // ==================== ENHANCED FNB ENDPOINTS ====================

  /**
   * Kitchen Stations
   */
  createKitchenStation: async (clubId, stationData) => {
    return await apiRequest(`/clubs/${clubId}/fnb/kitchen-stations`, {
      method: 'POST',
      body: JSON.stringify(stationData),
    });
  },

  getKitchenStations: async (clubId, activeOnly = false) => {
    const query = activeOnly ? '?activeOnly=true' : '';
    return await apiRequest(`/clubs/${clubId}/fnb/kitchen-stations${query}`);
  },

  getKitchenStation: async (clubId, stationId) => {
    return await apiRequest(`/clubs/${clubId}/fnb/kitchen-stations/${stationId}`);
  },

  updateKitchenStation: async (clubId, stationId, stationData) => {
    return await apiRequest(`/clubs/${clubId}/fnb/kitchen-stations/${stationId}`, {
      method: 'PATCH',
      body: JSON.stringify(stationData),
    });
  },

  deleteKitchenStation: async (clubId, stationId) => {
    return await apiRequest(`/clubs/${clubId}/fnb/kitchen-stations/${stationId}`, {
      method: 'DELETE',
    });
  },

  getStationStatistics: async (clubId, stationId) => {
    return await apiRequest(`/clubs/${clubId}/fnb/kitchen-stations/${stationId}/statistics`);
  },

  /**
   * Order Acceptance/Rejection
   */
  acceptOrder: async (clubId, orderId, stationId) => {
    return await apiRequest(`/clubs/${clubId}/fnb/orders/${orderId}/accept`, {
      method: 'POST',
      body: JSON.stringify({ stationId, isAccepted: true }),
    });
  },

  rejectOrder: async (clubId, orderId, reason) => {
    return await apiRequest(`/clubs/${clubId}/fnb/orders/${orderId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ rejectedReason: reason, isAccepted: false }),
    });
  },

  markOrderReady: async (clubId, orderId) => {
    return await apiRequest(`/clubs/${clubId}/fnb/orders/${orderId}/ready`, {
      method: 'POST',
    });
  },

  markOrderDelivered: async (clubId, orderId) => {
    return await apiRequest(`/clubs/${clubId}/fnb/orders/${orderId}/delivered`, {
      method: 'POST',
    });
  },

  /**
   * Menu Categories (Enhanced)
   */
  getAllMenuCategories: async (clubId) => {
    return await apiRequest(`/clubs/${clubId}/fnb/categories/all`);
  },

  createMenuCategory: async (clubId, categoryName) => {
    return await apiRequest(`/clubs/${clubId}/fnb/categories`, {
      method: 'POST',
      body: JSON.stringify({ categoryName }),
    });
  },

  deleteMenuCategory: async (clubId, categoryId) => {
    return await apiRequest(`/clubs/${clubId}/fnb/categories/${categoryId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Inventory Management
   */
  getInventory: async (clubId) => {
    return await apiRequest(`/clubs/${clubId}/fnb/inventory`);
  },

  createInventoryItem: async (clubId, inventoryData) => {
    return await apiRequest(`/clubs/${clubId}/fnb/inventory`, {
      method: 'POST',
      body: JSON.stringify(inventoryData),
    });
  },

  updateInventory: async (clubId, itemId, inventoryData) => {
    return await apiRequest(`/clubs/${clubId}/fnb/inventory/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(inventoryData),
    });
  },

  /**
   * Suppliers
   */
  getSuppliers: async (clubId, activeOnly = false) => {
    const query = activeOnly ? '?activeOnly=true' : '';
    return await apiRequest(`/clubs/${clubId}/fnb/suppliers${query}`);
  },

  createSupplier: async (clubId, supplierData) => {
    return await apiRequest(`/clubs/${clubId}/fnb/suppliers`, {
      method: 'POST',
      body: JSON.stringify(supplierData),
    });
  },

  updateSupplier: async (clubId, supplierId, supplierData) => {
    return await apiRequest(`/clubs/${clubId}/fnb/suppliers/${supplierId}`, {
      method: 'PATCH',
      body: JSON.stringify(supplierData),
    });
  },

  deleteSupplier: async (clubId, supplierId) => {
    return await apiRequest(`/clubs/${clubId}/fnb/suppliers/${supplierId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Delete menu item
   */
  deleteMenuItem: async (clubId, itemId) => {
    return await apiRequest(`/clubs/${clubId}/fnb/menu/${itemId}`, {
      method: 'DELETE',
    });
  },
};

// =============================================================================
// TOURNAMENTS API
// =============================================================================

export const tournamentsAPI = {
  /**
   * Get all tournaments
   */
  getTournaments: async (clubId, queryParams = {}) => {
    const params = new URLSearchParams();
    Object.entries(queryParams).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.append(k, String(v));
    });
    const qs = params.toString();
    const response = await apiRequest(`/clubs/${clubId}/tournaments${qs ? `?${qs}` : ''}`);
    // Handle response structure: { success: true, tournaments: [...] }
    return response?.tournaments || response || [];
  },

  /**
   * Get tournament by ID
   */
  getTournamentById: async (clubId, tournamentId) => {
    const response = await apiRequest(`/clubs/${clubId}/tournaments/${tournamentId}`);
    return response?.tournament || response;
  },

  /**
   * Create tournament
   */
  createTournament: async (clubId, tournamentData) => {
    return await apiRequest(`/clubs/${clubId}/tournaments`, {
      method: 'POST',
      body: JSON.stringify(tournamentData),
    });
  },

  /**
   * Update tournament
   */
  updateTournament: async (clubId, tournamentId, tournamentData) => {
    return await apiRequest(`/clubs/${clubId}/tournaments/${tournamentId}`, {
      method: 'PUT',
      body: JSON.stringify(tournamentData),
    });
  },

  /**
   * Delete tournament
   */
  deleteTournament: async (clubId, tournamentId) => {
    return await apiRequest(`/clubs/${clubId}/tournaments/${tournamentId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Start tournament
   */
  startTournament: async (clubId, tournamentId) => {
    return await apiRequest(`/clubs/${clubId}/tournaments/${tournamentId}/start`, {
      method: 'POST',
    });
  },

  /**
   * End tournament with winners
   */
  endTournament: async (clubId, tournamentId, winnersData) => {
    return await apiRequest(`/clubs/${clubId}/tournaments/${tournamentId}/end`, {
      method: 'POST',
      body: JSON.stringify(winnersData),
    });
  },

  /**
   * Get tournament players
   */
  getTournamentPlayers: async (clubId, tournamentId) => {
    return await apiRequest(`/clubs/${clubId}/tournaments/${tournamentId}/players`);
  },

  /**
   * Get tournament winners
   */
  getTournamentWinners: async (clubId, tournamentId) => {
    return await apiRequest(`/clubs/${clubId}/tournaments/${tournamentId}/winners`);
  },

  pauseTournament: async (clubId, tournamentId) => {
    return await apiRequest(`/clubs/${clubId}/tournaments/${tournamentId}/pause`, {
      method: 'POST',
    });
  },

  resumeTournament: async (clubId, tournamentId) => {
    return await apiRequest(`/clubs/${clubId}/tournaments/${tournamentId}/resume`, {
      method: 'POST',
    });
  },

  stopTournament: async (clubId, tournamentId) => {
    return await apiRequest(`/clubs/${clubId}/tournaments/${tournamentId}/stop`, {
      method: 'POST',
    });
  },

  exitTournamentPlayer: async (clubId, tournamentId, data) => {
    return await apiRequest(`/clubs/${clubId}/tournaments/${tournamentId}/exit-player`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  rebuyTournamentPlayer: async (clubId, tournamentId, data) => {
    return await apiRequest(`/clubs/${clubId}/tournaments/${tournamentId}/rebuy`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  increaseBlind: async (clubId, tournamentId, data) => {
    return await apiRequest(`/clubs/${clubId}/tournaments/${tournamentId}/increase-blind`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// =============================================================================
// STAFF API
// =============================================================================

export const staffAPI = {
  /**
   * Get all staff
   */
  getStaff: async (clubId) => {
    return await apiRequest(`/clubs/${clubId}/staff`);
  },

  /**
   * Create staff member
   */
  createStaff: async (clubId, staffData) => {
    return await apiRequest(`/clubs/${clubId}/staff`, {
      method: 'POST',
      body: JSON.stringify(staffData),
    });
  },
  
  /**
   * Get all staff with filters (Enhanced)
   */
  getAllStaffMembers: async (clubId, filters = {}) => {
    const params = new URLSearchParams();
    if (filters.role) params.append('role', filters.role);
    if (filters.status) params.append('status', filters.status);
    if (filters.search) params.append('search', filters.search);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
    
    const queryString = params.toString();
    return await apiRequest(`/clubs/${clubId}/staff-management${queryString ? '?' + queryString : ''}`);
  },

  /**
   * Create staff member with KYC (Enhanced)
   */
  createStaffMember: async (clubId, staffData) => {
    return await apiRequest(`/clubs/${clubId}/staff-management/create`, {
      method: 'POST',
      body: JSON.stringify(staffData),
    });
  },

  /**
   * Get staff member by ID
   */
  getStaffMember: async (clubId, staffId) => {
    return await apiRequest(`/clubs/${clubId}/staff-management/${staffId}`);
  },

  /**
   * Update staff member
   */
  updateStaffMember: async (clubId, staffId, staffData) => {
    return await apiRequest(`/clubs/${clubId}/staff-management/${staffId}`, {
      method: 'PUT',
      body: JSON.stringify(staffData),
    });
  },

  /**
   * Update staff salary
   */
  updateStaffSalary: async (clubId, staffId, data) => {
    return await apiRequest(`/clubs/${clubId}/staff-management/${staffId}/salary`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * Suspend staff member
   */
  suspendStaffMember: async (clubId, staffId, reason) => {
    return await apiRequest(`/clubs/${clubId}/staff-management/${staffId}/suspend`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },

  /**
   * Reactivate staff member
   */
  reactivateStaffMember: async (clubId, staffId) => {
    return await apiRequest(`/clubs/${clubId}/staff-management/${staffId}/reactivate`, {
      method: 'POST',
    });
  },

  /**
   * Delete staff member
   */
  deleteStaffMember: async (clubId, staffId) => {
    return await apiRequest(`/clubs/${clubId}/staff-management/${staffId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Reset staff password
   */
  resetStaffPassword: async (clubId, staffId) => {
    return await apiRequest(`/clubs/${clubId}/staff-management/${staffId}/reset-password`, {
      method: 'POST',
    });
  },

  /**
   * Update staff member
   */
  updateStaff: async (clubId, staffId, staffData) => {
    return await apiRequest(`/clubs/${clubId}/staff/${staffId}`, {
      method: 'PUT',
      body: JSON.stringify(staffData),
    });
  },

  /**
   * Get all chatable users for a club (staff + Super Admin + Admin users)
   */
  getAllChatableUsers: async (clubId) => {
    return await apiRequest(`/clubs/${clubId}/chat/chatable-users`);
  },
};

// =============================================================================
// SHIFT MANAGEMENT API
// =============================================================================

export const shiftsAPI = {
  /**
   * Get all shifts with optional filters
   */
  getShifts: async (clubId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return await apiRequest(`/clubs/${clubId}/shifts${query ? '?' + query : ''}`);
  },

  /**
   * Get shift by ID
   */
  getShiftById: async (clubId, shiftId) => {
    return await apiRequest(`/clubs/${clubId}/shifts/${shiftId}`);
  },

  /**
   * Create a new shift
   */
  createShift: async (clubId, shiftData) => {
    return await apiRequest(`/clubs/${clubId}/shifts`, {
      method: 'POST',
      body: JSON.stringify(shiftData),
    });
  },

  /**
   * Update a shift
   */
  updateShift: async (clubId, shiftId, shiftData) => {
    return await apiRequest(`/clubs/${clubId}/shifts/${shiftId}`, {
      method: 'PATCH',
      body: JSON.stringify(shiftData),
    });
  },

  /**
   * Delete a shift
   */
  deleteShift: async (clubId, shiftId) => {
    return await apiRequest(`/clubs/${clubId}/shifts/${shiftId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Copy shifts to new dates
   */
  copyShifts: async (clubId, copyData) => {
    return await apiRequest(`/clubs/${clubId}/shifts/copy`, {
      method: 'POST',
      body: JSON.stringify(copyData),
    });
  },

  /**
   * Get all dealers for shift assignment
   * @param {string} clubId - Club ID
   * @param {string} date - Optional date (YYYY-MM-DD) to filter out dealers on leave
   */
  getDealers: async (clubId, date) => {
    const query = date ? `?date=${date}` : '';
    return await apiRequest(`/clubs/${clubId}/shifts-dealers${query}`);
  },

  /**
   * Get available dealers for a specific date (have shift and not on leave)
   */
  getAvailableDealersForDate: async (clubId, date) => {
    return await apiRequest(`/clubs/${clubId}/shifts/available-dealers?date=${date}`);
  },

  /**
   * Delete multiple shifts
   */
  deleteMultipleShifts: async (clubId, shiftIds) => {
    return await apiRequest(`/clubs/${clubId}/shifts/delete-multiple`, {
      method: 'POST',
      body: JSON.stringify({ shiftIds }),
    });
  },
};

// =============================================================================
// PAYROLL API
// =============================================================================

export const payrollAPI = {
  // Salary Processing
  processSalary: async (clubId, salaryData) => {
    return await apiRequest(`/clubs/${clubId}/payroll/salary`, {
      method: 'POST',
      body: JSON.stringify(salaryData),
    });
  },

  getSalaryPayments: async (clubId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return await apiRequest(`/clubs/${clubId}/payroll/salary${query ? '?' + query : ''}`);
  },

  getSalaryPaymentById: async (clubId, paymentId) => {
    return await apiRequest(`/clubs/${clubId}/payroll/salary/${paymentId}`);
  },

  getAllStaffForPayroll: async (clubId) => {
    return await apiRequest(`/clubs/${clubId}/payroll/staff`);
  },

  getDealersForPayroll: async (clubId) => {
    return await apiRequest(`/clubs/${clubId}/payroll/dealers`);
  },

  // Dealer Tips
  getTipSettings: async (clubId, dealerId) => {
    const query = dealerId ? `?dealerId=${dealerId}` : '';
    return await apiRequest(`/clubs/${clubId}/payroll/tips/settings${query}`);
  },

  updateTipSettings: async (clubId, settings, dealerId) => {
    const query = dealerId ? `?dealerId=${dealerId}` : '';
    return await apiRequest(`/clubs/${clubId}/payroll/tips/settings${query}`, {
      method: 'POST',
      body: JSON.stringify(settings),
    });
  },

  processDealerTips: async (clubId, tipsData) => {
    return await apiRequest(`/clubs/${clubId}/payroll/tips`, {
      method: 'POST',
      body: JSON.stringify(tipsData),
    });
  },

  getDealerTips: async (clubId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return await apiRequest(`/clubs/${clubId}/payroll/tips${query ? '?' + query : ''}`);
  },

  getDealerTipsSummary: async (clubId, dealerId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return await apiRequest(`/clubs/${clubId}/payroll/tips/${dealerId}/summary${query ? '?' + query : ''}`);
  },

  // Dealer Cashouts
  processDealerCashout: async (clubId, cashoutData) => {
    return await apiRequest(`/clubs/${clubId}/payroll/cashout`, {
      method: 'POST',
      body: JSON.stringify(cashoutData),
    });
  },

  getDealerCashouts: async (clubId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return await apiRequest(`/clubs/${clubId}/payroll/cashout${query ? '?' + query : ''}`);
  },

  // Managers
  getManagersForPayroll: async (clubId) => {
    return await apiRequest(`/clubs/${clubId}/payroll/managers`);
  },

  // Manager Tip Balance
  getManagerTipBalance: async (clubId, managerId) => {
    return await apiRequest(`/clubs/${clubId}/payroll/manager-tips/${managerId}/balance`);
  },

  // Manager Cashouts
  processManagerCashout: async (clubId, cashoutData) => {
    return await apiRequest(`/clubs/${clubId}/payroll/manager-cashout`, {
      method: 'POST',
      body: JSON.stringify(cashoutData),
    });
  },

  getManagerCashouts: async (clubId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return await apiRequest(`/clubs/${clubId}/payroll/manager-cashout${query ? '?' + query : ''}`);
  },

  // Club Tip Summary
  getClubTipSummary: async (clubId) => {
    return await apiRequest(`/clubs/${clubId}/payroll/tips/club-summary`);
  },
};

// =============================================================================
// BONUS MANAGEMENT API
// =============================================================================

export const bonusAPI = {
  // Player Bonuses
  processPlayerBonus: async (clubId, bonusData) => {
    return await apiRequest(`/clubs/${clubId}/bonuses/players`, {
      method: 'POST',
      body: JSON.stringify(bonusData),
    });
  },

  getPlayerBonuses: async (clubId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return await apiRequest(`/clubs/${clubId}/bonuses/players${query ? '?' + query : ''}`);
  },

  getPlayersForBonus: async (clubId, search) => {
    const query = search ? `?search=${encodeURIComponent(search)}` : '';
    return await apiRequest(`/clubs/${clubId}/bonuses/players/list${query}`);
  },

  // Staff Bonuses
  processStaffBonus: async (clubId, bonusData) => {
    return await apiRequest(`/clubs/${clubId}/bonuses/staff`, {
      method: 'POST',
      body: JSON.stringify(bonusData),
    });
  },

  getStaffBonuses: async (clubId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return await apiRequest(`/clubs/${clubId}/bonuses/staff${query ? '?' + query : ''}`);
  },

  getStaffForBonus: async (clubId, search) => {
    const query = search ? `?search=${encodeURIComponent(search)}` : '';
    return await apiRequest(`/clubs/${clubId}/bonuses/staff/list${query}`);
  },
};

// Affiliate Management
export const affiliateAPI = {
  getAffiliates: async (clubId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return await apiRequest(`/clubs/${clubId}/affiliates${query ? '?' + query : ''}`);
  },

  getAffiliateReferrals: async (clubId, affiliateId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return await apiRequest(`/clubs/${clubId}/affiliates/${affiliateId}/referrals${query ? '?' + query : ''}`);
  },

  processAffiliatePayment: async (clubId, paymentData) => {
    return await apiRequest(`/clubs/${clubId}/affiliates/payments`, {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
  },

  getAffiliateTransactions: async (clubId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return await apiRequest(`/clubs/${clubId}/affiliates/transactions${query ? '?' + query : ''}`);
  },

  /**
   * Override an existing affiliate transaction (Super Admin / Admin only)
   */
  overrideAffiliateTransaction: async (clubId, transactionId, data) => {
    return await apiRequest(`/clubs/${clubId}/affiliates/transactions/${transactionId}/override`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
};

// Financial Overrides
export const financialOverridesAPI = {
  getAllTransactions: async (clubId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return await apiRequest(`/clubs/${clubId}/financial-overrides/transactions${query ? '?' + query : ''}`);
  },

  editTransaction: async (clubId, transactionId, data) => {
    return await apiRequest(`/clubs/${clubId}/financial-overrides/transactions/${transactionId}/edit`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  cancelTransaction: async (clubId, transactionId, data = {}) => {
    return await apiRequest(`/clubs/${clubId}/financial-overrides/transactions/${transactionId}/cancel`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// =============================================================================
// TRANSACTIONS API
// =============================================================================

export const transactionsAPI = {
  /**
   * Get all transactions
   */
  getTransactions: async (clubId, filters = {}) => {
    const query = new URLSearchParams(filters).toString();
    return await apiRequest(`/clubs/${clubId}/transactions${query ? '?' + query : ''}`);
  },

  /**
   * Create transaction
   */
  createTransaction: async (clubId, transactionData) => {
    return await apiRequest(`/clubs/${clubId}/transactions`, {
      method: 'POST',
      body: JSON.stringify(transactionData),
    });
  },
};

// =============================================================================
// TENANTS API (Master Admin)
// =============================================================================

export const tenantsAPI = {
  /**
   * Get all tenants
   */
  getTenants: async () => {
    return await apiRequest('/tenants');
  },

  /**
   * Get tenant by ID with super admin info
   */
  getTenant: async (tenantId) => {
    return await apiRequest(`/tenants/${tenantId}`, {
      method: 'GET',
    });
  },

  /**
   * Create tenant (only tenant + super admin, no club)
   */
  createTenant: async (data) => {
    // If data is a string, convert to object for backward compatibility
    const payload = typeof data === 'string' ? { name: data } : data;
    
    return await apiRequest('/tenants', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Create super admin for tenant
   */
  createSuperAdmin: async (tenantId, email, displayName) => {
    return await apiRequest(`/tenants/${tenantId}/super-admins`, {
      method: 'POST',
      body: JSON.stringify({ email, displayName }),
    });
  },

  /**
   * Setup tenant (create tenant + club + super admin in one go)
   */
  setupTenant: async (tenantId, data) => {
    return await apiRequest(`/tenants/${tenantId}/setup`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Create club under tenant
   */
  createClub: async (tenantId, clubData) => {
    return await apiRequest(`/tenants/${tenantId}/clubs`, {
      method: 'POST',
      body: JSON.stringify(clubData),
    });
  },

  /**
   * Create club with branding for existing tenant
   */
  createClubWithBranding: async (tenantId, clubData) => {
    // Filter out empty/undefined values
    const payload = {
      name: clubData.name,
    };
    
    if (clubData.description) payload.description = clubData.description;
    if (clubData.skinColor) payload.skinColor = clubData.skinColor;
    if (clubData.gradient) payload.gradient = clubData.gradient;
    if (clubData.logoUrl) payload.logoUrl = clubData.logoUrl;
    if (clubData.videoUrl) payload.videoUrl = clubData.videoUrl;
    if (clubData.pokerEnabled !== undefined) payload.pokerEnabled = clubData.pokerEnabled;
    if (clubData.rummyEnabled !== undefined) payload.rummyEnabled = clubData.rummyEnabled;
    
    return await apiRequest(`/tenants/${tenantId}/clubs`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Get signed URL for logo upload
   */
  getLogoUploadUrl: async (tenantId, clubId) => {
    return await apiRequest(`/tenants/${tenantId}/clubs/${clubId}/logo-upload-url`, {
      method: 'POST',
    });
  },

  /**
   * Upload logo file to Supabase
   */
  uploadLogo: async (signedUrl, file) => {
    const response = await fetch(signedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': 'image/png',
      },
      body: file,
    });
    
    if (!response.ok) {
      throw new Error('Failed to upload logo');
    }
    
    return response;
  },

  /**
   * Get logo public URL from backend
   */
  getLogoPublicUrl: async (tenantId, clubId) => {
    const response = await apiRequest(`/tenants/${tenantId}/clubs/${clubId}/logo-url`);
    return response.logoUrl;
  },

  /**
   * Reset tenant Super Admin password (Master Admin only)
   */
  resetTenantSuperAdminPassword: async (tenantId) => {
    return await apiRequest(`/tenants/${tenantId}/reset-super-admin-password`, {
      method: 'POST',
    });
  },

  /**
   * Permanently delete tenant (Master Admin only)
   */
  deleteTenant: async (tenantId, options = {}) => {
    return await apiRequest(`/tenants/${tenantId}`, {
      method: 'DELETE',
      body: JSON.stringify(options),
    });
  },
};

// =============================================================================
// MASTER ADMIN API
// =============================================================================

export const masterAdminAPI = {
  /**
   * Get current logged-in user info
   */
  getCurrentUser: async () => {
    const userId = localStorage.getItem(STORAGE_KEYS.USER_ID);
    if (!userId) {
      throw new Error('User not logged in');
    }
    // Fetch user from backend
    return await apiRequest(`/users/${userId}`);
  },

  /**
   * Get all clubs with tenant info
   */
  getAllClubs: async () => {
    return await apiRequest('/clubs/master-admin/all');
  },

  /**
   * Update club status (active/suspended/killed)
   */
  updateClubStatus: async (clubId, status, reason) => {
    return await apiRequest(`/clubs/${clubId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status, reason }),
    });
  },

  /**
   * Update club details (name, logo, branding)
   */
  updateClubDetails: async (clubId, details) => {
    return await apiRequest(`/clubs/${clubId}/master-admin/details`, {
      method: 'PUT',
      body: JSON.stringify(details),
    });
  },

  /**
   * Update club subscription
   */
  updateClubSubscription: async (clubId, subscriptionData) => {
    return await apiRequest(`/clubs/${clubId}/subscription`, {
      method: 'PUT',
      body: JSON.stringify(subscriptionData),
    });
  },

  /**
   * Update club terms and conditions
   */
  updateClubTerms: async (clubId, termsAndConditions) => {
    return await apiRequest(`/clubs/${clubId}/terms`, {
      method: 'PUT',
      body: JSON.stringify({ termsAndConditions }),
    });
  },

  /**
   * Update club rummy enabled status
   */
  updateClubRummyEnabled: async (clubId, rummyEnabled) => {
    return await apiRequest(`/clubs/${clubId}/rummy-enabled`, {
      method: 'PUT',
      body: JSON.stringify({ rummyEnabled }),
    });
  },

  /**
   * Update club game access (poker, rummy, tournaments toggles)
   */
  updateClubGameAccess: async (clubId, updates) => {
    return await apiRequest(`/clubs/${clubId}/game-access`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  /**
   * Get club game access settings
   */
  getClubGameAccess: async (clubId) => {
    return await apiRequest(`/clubs/${clubId}/game-access`);
  },

  /**
   * Create complete tenant setup (tenant + club + super admin)
   */
  createTenantWithClub: async (data) => {
    return await apiRequest('/tenants/with-club', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Get dashboard statistics
   */
  getDashboardStats: async () => {
    try {
      const clubs = await masterAdminAPI.getAllClubs();
      const tenants = await tenantsAPI.getTenants();

      // Calculate statistics
      const totalClubs = clubs.length;
      const activeClubs = clubs.filter(c => c.status === 'active').length;
      const suspendedClubs = clubs.filter(c => c.status === 'suspended').length;
      const killedClubs = clubs.filter(c => c.status === 'killed').length;

      // Calculate revenue
      const monthlyRevenue = clubs.reduce((sum, club) => {
        return sum + (parseFloat(club.subscriptionPrice) || 0);
      }, 0);

      return {
        totalTenants: tenants.length,
        totalClubs,
        activeClubs,
        suspendedClubs,
        killedClubs,
        monthlyRevenue,
        clubs,
        tenants,
      };
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
      // Return empty data structure on error
      return {
        totalTenants: 0,
        totalClubs: 0,
        activeClubs: 0,
        suspendedClubs: 0,
        killedClubs: 0,
        monthlyRevenue: 0,
        clubs: [],
        tenants: [],
      };
    }
  },
};

/**
 * Super Admin APIs
 */
export const superAdminAPI = {
  // Get all clubs for super admin
  getClubs: async () => {
    // Check both 'user' and 'superadminuser' storage keys
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const superAdminUser = JSON.parse(localStorage.getItem('superadminuser') || '{}');
    const userId = user.id || superAdminUser.userId;
    
    if (!userId) {
      throw new Error('User not logged in');
    }
    return await apiRequest(`/users/${userId}/clubs`);
  },

  // Get club details
  getClub: async (clubId) => {
    return await apiRequest(`/clubs/${clubId}`);
  },

  // Get club revenue data
  getClubRevenue: async (clubId) => {
    return await apiRequest(`/clubs/${clubId}/revenue`);
  },

  // Player Management
  getAllPlayers: async (clubId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return await apiRequest(`/clubs/${clubId}/players?${queryString}`);
  },

  createPlayer: async (clubId, playerData) => {
    return await apiRequest(`/clubs/${clubId}/players`, {
      method: 'POST',
      body: JSON.stringify(playerData),
    });
  },

  deletePlayer: async (clubId, playerId) => {
    return await apiRequest(`/clubs/${clubId}/players/${playerId}`, {
      method: 'DELETE',
    });
  },

  getPendingApprovalPlayers: async (clubId) => {
    return await apiRequest(`/clubs/${clubId}/players-pending-approval`);
  },

  approvePlayer: async (clubId, playerId, notes) => {
    return await apiRequest(`/clubs/${clubId}/players/${playerId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  },

  rejectPlayer: async (clubId, playerId, reason) => {
    return await apiRequest(`/clubs/${clubId}/players/${playerId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },

  suspendPlayer: async (clubId, playerId, data) => {
    return await apiRequest(`/clubs/${clubId}/players/${playerId}/suspend`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  unsuspendPlayer: async (clubId, playerId) => {
    return await apiRequest(`/clubs/${clubId}/players/${playerId}/unsuspend`, {
      method: 'POST',
    });
  },

  resetPlayerPassword: async (clubId, playerId) => {
    return await apiRequest(`/clubs/${clubId}/players/${playerId}/reset-password`, {
      method: 'POST',
    });
  },

  getSuspendedPlayers: async (clubId) => {
    return await apiRequest(`/clubs/${clubId}/players-suspended`);
  },

  getPlayer: async (clubId, playerId) => {
    return await apiRequest(`/clubs/${clubId}/players/${playerId}`);
  },

  updatePlayer: async (clubId, playerId, playerData) => {
    return await apiRequest(`/clubs/${clubId}/players/${playerId}`, {
      method: 'PUT',
      body: JSON.stringify(playerData),
    });
  },

  // Get all players (alias for getAllPlayers)
  getPlayers: async (clubId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return await apiRequest(`/clubs/${clubId}/players?${queryString}`);
  },

  // Get player balance
  getPlayerBalance: async (clubId, playerId) => {
    return await apiRequest(`/clubs/${clubId}/players/${playerId}/balance`);
  },

  // Get seated players (for buy-out)
  getSeatedPlayers: async (clubId) => {
    return await apiRequest(`/clubs/${clubId}/seated-players`);
  },

  // Transactions
  getTransactions: async (clubId, filters = {}) => {
    const query = new URLSearchParams(filters).toString();
    return await apiRequest(`/clubs/${clubId}/transactions${query ? '?' + query : ''}`);
  },

  createTransaction: async (clubId, transactionData) => {
    return await apiRequest(`/clubs/${clubId}/transactions`, {
      method: 'POST',
      body: JSON.stringify(transactionData),
    });
  },

  /** Table Buy Out + credit settlement (not raw Deposit) — staff Process Buy-Out */
  manualTableBuyOut: async (clubId, body) => {
    return await apiRequest(`/clubs/${clubId}/manual-table-buy-out`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  // Credit Management
  getCreditRequests: async (clubId, status = null) => {
    const query = status ? `?status=${status}` : '';
    return await apiRequest(`/clubs/${clubId}/credit-requests${query}`);
  },

  approveCreditRequest: async (clubId, requestId, data = {}) => {
    return await apiRequest(`/clubs/${clubId}/credit-requests/${requestId}/approve`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  rejectCreditRequest: async (clubId, requestId, data) => {
    return await apiRequest(`/clubs/${clubId}/credit-requests/${requestId}/deny`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  unlockCredit: async (clubId, playerId, data) => {
    return await apiRequest(`/clubs/${clubId}/players/${playerId}/enable-credit`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateCreditLimit: async (clubId, playerId, data) => {
    return await apiRequest(`/clubs/${clubId}/players/${playerId}/credit-limit`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  /** Lock (disable) player credit — super admin only; backend enforces zero outstanding + no table credit. */
  lockCredit: async (clubId, playerId) => {
    return await apiRequest(`/clubs/${clubId}/players/${playerId}/disable-credit`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },

  /** Paginated credit-related ledger events (draws, debits, club buy-in, table buy-out, credit-line table buy-in) */
  getCreditSessionHistory: async (clubId, { q, from, to, page = 1, limit = 10 } = {}) => {
    const params = new URLSearchParams();
    if (q) params.append('q', q);
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    params.append('page', String(page));
    params.append('limit', String(limit));
    return await apiRequest(`/clubs/${clubId}/credit-session-history?${params.toString()}`);
  },

  // VIP Store Management
  getVipProducts: async (clubId) => {
    return await apiRequest(`/clubs/${clubId}/vip-products`);
  },

  createVipProduct: async (clubId, productData) => {
    return await apiRequest(`/clubs/${clubId}/vip-products`, {
      method: 'POST',
      body: JSON.stringify(productData),
    });
  },

  updateVipProduct: async (clubId, productId, productData) => {
    return await apiRequest(`/clubs/${clubId}/vip-products/${productId}`, {
      method: 'PUT',
      body: JSON.stringify(productData),
    });
  },

  deleteVipProduct: async (clubId, productId) => {
    return await apiRequest(`/clubs/${clubId}/vip-products/${productId}`, {
      method: 'DELETE',
    });
  },

  // VIP Purchases (Admin view)
  getVipPurchases: async (clubId, page = 1, limit = 10) => {
    return await apiRequest(`/clubs/${clubId}/vip-purchases?page=${page}&limit=${limit}`);
  },

  // VIP Product Image Upload
  createVipProductImageUploadUrl: async (clubId, filename) => {
    return await apiRequest(`/clubs/${clubId}/vip-products/upload-url`, {
      method: 'POST',
      body: JSON.stringify({ filename }),
    });
  },

  uploadToSignedUrl: async (signedUrl, file) => {
    try {
      // Supabase signed URLs require specific handling
      const response = await fetch(signedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'image/png',
          'Cache-Control': '3600',
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        console.error('Upload failed:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText,
          url: signedUrl.substring(0, 100) + '...',
        });
        throw new Error(`Upload failed (${response.status}): ${response.statusText}`);
      }
      
      return response;
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  },

  // Push Notifications
  getPushNotifications: async (clubId, notificationType = null) => {
    const query = notificationType ? `?notificationType=${notificationType}` : '';
    return await apiRequest(`/clubs/${clubId}/push-notifications${query}`);
  },

  createPushNotification: async (clubId, notificationData) => {
    return await apiRequest(`/clubs/${clubId}/push-notifications`, {
      method: 'POST',
      body: JSON.stringify(notificationData),
    });
  },

  updatePushNotification: async (clubId, notificationId, notificationData) => {
    return await apiRequest(`/clubs/${clubId}/push-notifications/${notificationId}`, {
      method: 'PUT',
      body: JSON.stringify(notificationData),
    });
  },

  deletePushNotification: async (clubId, notificationId) => {
    return await apiRequest(`/clubs/${clubId}/push-notifications/${notificationId}`, {
      method: 'DELETE',
    });
  },

  createPushNotificationUploadUrl: async (clubId, filename, isVideo = false) => {
    return await apiRequest(`/clubs/${clubId}/push-notifications/upload-url`, {
      method: 'POST',
      body: JSON.stringify({ filename, isVideo }),
    });
  },

  sendPushNotification: async (clubId, notificationId) => {
    return await apiRequest(`/clubs/${clubId}/push-notifications/${notificationId}/send`, {
      method: 'POST',
    });
  },

  getNotificationInbox: async (clubId, recipientType = 'staff', page = 1, limit = 10) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    // Always use user.id (auth user ID) for both staff and players
    const userId = user.id;
    if (!userId) throw new Error('User ID not found');
    return await apiRequest(`/clubs/${clubId}/notifications/inbox?recipientType=${recipientType}&page=${page}&limit=${limit}`, {
      headers: { 'x-user-id': userId },
    });
  },

  getUnreadNotificationCount: async (clubId, recipientType = 'staff') => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    // Always use user.id (auth user ID) for both staff and players
    const userId = user.id;
    if (!userId) throw new Error('User ID not found');
    return await apiRequest(`/clubs/${clubId}/notifications/unread-count?recipientType=${recipientType}`, {
      headers: { 'x-user-id': userId },
    });
  },

  markNotificationAsRead: async (clubId, notificationId, recipientType = 'staff') => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    // Always use user.id (auth user ID) for both staff and players
    const userId = user.id;
    if (!userId) throw new Error('User ID not found');
    return await apiRequest(`/clubs/${clubId}/notifications/${notificationId}/mark-read`, {
      method: 'POST',
      headers: { 'x-user-id': userId },
      body: JSON.stringify({ recipientType }),
    });
  },

  markAllNotificationsAsRead: async (clubId, recipientType = 'staff') => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    // Always use user.id (auth user ID) for both staff and players
    const userId = user.id;
    if (!userId) throw new Error('User ID not found');
    return await apiRequest(`/clubs/${clubId}/notifications/mark-all-read`, {
      method: 'POST',
      headers: { 'x-user-id': userId },
      body: JSON.stringify({ recipientType }),
    });
  },

  // Player Feedback
  getPlayerFeedback: async (clubId, page = 1, limit = 10) => {
    return await apiRequest(`/clubs/${clubId}/player-feedback?page=${page}&limit=${limit}`);
  },

  // Player Field Update Requests
  getFieldUpdateRequests: async (clubId) => {
    return await apiRequest(`/clubs/${clubId}/player-field-updates/pending`);
  },

  approveFieldUpdate: async (clubId, requestId) => {
    return await apiRequest(`/clubs/${clubId}/player-field-updates/${requestId}/approve`, {
      method: 'POST',
    });
  },

  getPlayerDocuments: async (clubId, playerId) => {
    return await apiRequest(`/clubs/${clubId}/players/${playerId}/documents`);
  },

  rejectFieldUpdate: async (clubId, requestId, reason) => {
    return await apiRequest(`/clubs/${clubId}/player-field-updates/${requestId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },
};

// =============================================================================
// CHAT API
// =============================================================================

export const chatAPI = {
  // Staff Chat
  createStaffChatSession: async (clubId, recipientStaffId, subject) => {
    return await apiRequest(`/clubs/${clubId}/chat/staff/sessions`, {
      method: 'POST',
      body: JSON.stringify({ recipientStaffId, subject }),
    });
  },

  getStaffChatSessions: async (clubId, { page = 1, limit = 10, search, role } = {}) => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (search) params.append('search', search);
    if (role) params.append('role', role);
    const userId = localStorage.getItem('userId');
    const headers = userId ? { 'x-user-id': userId } : {};
    return await apiRequest(`/clubs/${clubId}/chat/staff/sessions?${params.toString()}`, {
      headers
    });
  },

  // Player Chat
  createPlayerChatSession: async (clubId, playerId, subject) => {
    return await apiRequest(`/clubs/${clubId}/chat/player/sessions`, {
      method: 'POST',
      body: JSON.stringify({ playerId, subject }),
    });
  },

  getPlayerChatSessions: async (clubId, { page = 1, limit = 10, status, search } = {}) => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (status) params.append('status', status);
    if (search) params.append('search', search);
    return await apiRequest(`/clubs/${clubId}/chat/player/sessions?${params.toString()}`);
  },

  // Messages
  sendMessage: async (clubId, sessionId, message) => {
    return await apiRequest(`/clubs/${clubId}/chat/sessions/${sessionId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
  },

  getSessionMessages: async (clubId, sessionId, { page = 1, limit = 50 } = {}) => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    return await apiRequest(`/clubs/${clubId}/chat/sessions/${sessionId}/messages?${params.toString()}`);
  },

  // Session management
  updateChatSession: async (clubId, sessionId, updates) => {
    return await apiRequest(`/clubs/${clubId}/chat/sessions/${sessionId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  },

  getUnreadCounts: async (clubId) => {
    return await apiRequest(`/clubs/${clubId}/chat/unread-counts`);
  },

  // Get all chatable users (staff + Super Admin + Admin)
  getChatableUsers: async (clubId, excludeExisting = false) => {
    const queryParam = excludeExisting ? '?excludeExisting=true' : '';
    return await apiRequest(`/clubs/${clubId}/chat/chatable-users${queryParam}`);
  },

  // Archive chat session (one-sided deletion)
  archiveChatSession: async (clubId, sessionId) => {
    return await apiRequest(`/clubs/${clubId}/chat/sessions/${sessionId}`, {
      method: 'DELETE',
    });
  },
};

// =============================================================================
// LEAVE MANAGEMENT API
// =============================================================================

const LEAVE_LIST_KEYS = [
  'applications',
  'leaveApplications',
  'leaves',
  'items',
  'results',
  'rows',
  'list',
  'records',
  'docs',
  'content',
  'body',
];

/** Unwrap nested { data: ... } envelopes (common with success/payload wrappers). */
function unwrapLeavePayload(payload, maxDepth = 6) {
  let p = payload;
  let depth = 0;
  while (
    p &&
    typeof p === 'object' &&
    !Array.isArray(p) &&
    Object.prototype.hasOwnProperty.call(p, 'data') &&
    p.data != null &&
    depth < maxDepth
  ) {
    p = p.data;
    depth++;
  }
  return p;
}

function firstLeaveApplicationsArray(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
  for (let i = 0; i < LEAVE_LIST_KEYS.length; i++) {
    const k = LEAVE_LIST_KEYS[i];
    if (Array.isArray(obj[k])) return obj[k];
  }
  return null;
}

/** Backend may return a bare array, nested data, or various list property names. */
function normalizePendingLeaveApplicationsResponse(payload) {
  if (payload == null) return [];
  let p = unwrapLeavePayload(payload);
  if (Array.isArray(p)) return p;
  if (typeof p !== 'object' || p === null) return [];
  const arr = firstLeaveApplicationsArray(p);
  return Array.isArray(arr) ? arr : [];
}

/** Backend may return paginated object with varying field names or nested data. */
function normalizeLeaveApplicationsForApprovalResponse(payload, fallbackLimit = 10) {
  const empty = { applications: [], total: 0, totalPages: 0, page: 1 };
  if (payload == null) return empty;
  let p = unwrapLeavePayload(payload);
  if (Array.isArray(p)) {
    return {
      applications: p,
      total: p.length,
      totalPages: 1,
      page: 1,
    };
  }
  if (typeof p !== 'object' || p === null) return empty;
  const applications = firstLeaveApplicationsArray(p) || [];
  const total =
    Number(p.total ?? p.totalCount ?? p.totalRecords ?? p.count ?? applications.length) ||
    applications.length;
  const limit = Number(p.limit ?? p.pageSize ?? fallbackLimit) || fallbackLimit;
  const page = Number(p.page ?? p.currentPage ?? p.pageNumber ?? 1) || 1;
  const totalPages =
    Number(p.totalPages ?? p.pages ?? Math.max(1, Math.ceil(total / limit))) || 1;
  return { applications, total, totalPages, page };
}

export const leaveAPI = {
  /**
   * Get all leave policies
   */
  getLeavePolicies: async (clubId) => {
    return await apiRequest(`/clubs/${clubId}/leave-policies`, { clubIdOverride: clubId });
  },

  /**
   * Create leave policy
   */
  createLeavePolicy: async (clubId, policyData) => {
    return await apiRequest(`/clubs/${clubId}/leave-policies`, {
      method: 'POST',
      body: JSON.stringify(policyData),
      clubIdOverride: clubId,
    });
  },

  /**
   * Update leave policy
   */
  updateLeavePolicy: async (clubId, role, policyData) => {
    return await apiRequest(`/clubs/${clubId}/leave-policies/${encodeURIComponent(role)}`, {
      method: 'PUT',
      body: JSON.stringify(policyData),
      clubIdOverride: clubId,
    });
  },

  /**
   * Delete leave policy
   */
  deleteLeavePolicy: async (clubId, role) => {
    return await apiRequest(`/clubs/${clubId}/leave-policies/${encodeURIComponent(role)}`, {
      method: 'DELETE',
      clubIdOverride: clubId,
    });
  },

  /**
   * Get my leave applications with filters and pagination
   */
  getMyLeaveApplications: async (clubId, filters = {}) => {
    const queryParams = new URLSearchParams();
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.startDate) queryParams.append('startDate', filters.startDate);
    if (filters.endDate) queryParams.append('endDate', filters.endDate);
    if (filters.page) queryParams.append('page', filters.page.toString());
    if (filters.limit) queryParams.append('limit', filters.limit.toString());
    
    const query = queryParams.toString();
    return await apiRequest(`/clubs/${clubId}/leave-applications/my-leaves${query ? '?' + query : ''}`, {
      clubIdOverride: clubId,
    });
  },

  /**
   * Get leave balance
   */
  getLeaveBalance: async (clubId) => {
    return await apiRequest(`/clubs/${clubId}/leave-applications/balance`, { clubIdOverride: clubId });
  },

  /**
   * Get pending leave applications (for approvers)
   */
  getPendingLeaveApplications: async (clubId) => {
    const raw = await apiRequest(`/clubs/${clubId}/leave-applications/pending`, { clubIdOverride: clubId });
    return normalizePendingLeaveApplicationsResponse(raw);
  },

  /**
   * Get leave applications for approval with filters and pagination
   */
  getLeaveApplicationsForApproval: async (clubId, filters = {}) => {
    const queryParams = new URLSearchParams();
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.role) queryParams.append('role', filters.role);
    if (filters.startDate) queryParams.append('startDate', filters.startDate);
    if (filters.endDate) queryParams.append('endDate', filters.endDate);
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.page) queryParams.append('page', filters.page.toString());
    if (filters.limit) queryParams.append('limit', filters.limit.toString());
    
    const query = queryParams.toString();
    const raw = await apiRequest(`/clubs/${clubId}/leave-applications/for-approval${query ? '?' + query : ''}`, {
      clubIdOverride: clubId,
    });
    return normalizeLeaveApplicationsForApprovalResponse(raw, filters.limit || 10);
  },

  /**
   * Create leave application
   */
  createLeaveApplication: async (clubId, applicationData) => {
    return await apiRequest(`/clubs/${clubId}/leave-applications`, {
      method: 'POST',
      body: JSON.stringify(applicationData),
      clubIdOverride: clubId,
    });
  },

  /**
   * Approve leave application
   */
  approveLeaveApplication: async (clubId, applicationId) => {
    return await apiRequest(`/clubs/${clubId}/leave-applications/${applicationId}/approve`, {
      method: 'POST',
      clubIdOverride: clubId,
    });
  },

  /**
   * Reject leave application
   */
  rejectLeaveApplication: async (clubId, applicationId, rejectionReason) => {
    return await apiRequest(`/clubs/${clubId}/leave-applications/${applicationId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ rejectionReason }),
      clubIdOverride: clubId,
    });
  },

  /**
   * Cancel leave application (by employee)
   */
  cancelLeaveApplication: async (clubId, applicationId) => {
    return await apiRequest(`/clubs/${clubId}/leave-applications/${applicationId}/cancel`, {
      method: 'POST',
      clubIdOverride: clubId,
    });
  },
};

// Export storage keys for use in components
export { STORAGE_KEYS };

